'use client'

/**
 * Inspired by https://github.com/asionesjia/useGoogleAuth/blob/main/src/hooks/use-google-auth.tsx
 *   * Fixed some types
 *   * Allows injecting Google Client ID and Redirect URI instead of using env vars
 * */

import { useEffect, useState } from 'react'

interface CredentialResponse {
  credential: string
}

interface FedCMCredential {
  token: string
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            auto_select?: boolean
            callback: (response: CredentialResponse) => void
            cancel_on_tap_outside?: boolean
            client_id: string
          }) => void
          prompt: () => void
        }
      }
    }
  }
}

const isFedCMSupported = (): boolean => {
  try {
    return (
      typeof window !== 'undefined' &&
      'IdentityCredential' in window &&
      'navigator' in window &&
      'credentials' in navigator &&
      !!navigator.credentials.get
    )
  } catch {
    return false
  }
}

const isOneTapSupported = (): boolean => {
  return typeof window !== 'undefined' && !!window.google?.accounts?.id
}

export const useGoogleAuth = ({
  disableFedCM,
  googleFedcmClientId,
  googleOauthClientId,
  googleOauthRedirectUri,
}: {
  disableFedCM?: boolean
  googleFedcmClientId: string
  googleOauthClientId: string
  googleOauthRedirectUri: string
}) => {
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isFedCMAvailable, setIsFedCMAvailable] = useState<boolean>(false)
  const [isOneTapAvailable, setIsOneTapAvailable] = useState<boolean>(false)
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] =
    useState<boolean>(false)
  const [isFedCMAuthenticating, setIsFedCMAuthenticating] =
    useState<boolean>(false)
  const [hasAttemptedAutoLogin, setHasAttemptedAutoLogin] =
    useState<boolean>(false) // New flag

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => {
      setIsGoogleScriptLoaded(true)
    }
    script.onerror = () => {
      setError('Failed to load Google script')
    }
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  // Handle authentication logic (only attempt once)
  useEffect(() => {
    if (
      !isGoogleScriptLoaded ||
      isFedCMAuthenticating ||
      Boolean(token) ||
      hasAttemptedAutoLogin ||
      disableFedCM // Prevent retry after first attempt
    ) {
      return
    }

    const fedCMSupported = isFedCMSupported()
    const oneTapSupported = isOneTapSupported()

    setIsFedCMAvailable(fedCMSupported)
    setIsOneTapAvailable(oneTapSupported)

    if (fedCMSupported) {
      authenticateWithFedCM()
    } else if (oneTapSupported) {
      initializeGoogleOneTap()
    } else {
      setError('FedCM and One Tap unavailable, please use OAuth')
      setHasAttemptedAutoLogin(true) // Mark attempt as done even if no methods are available
    }
  }, [
    isGoogleScriptLoaded,
    isFedCMAuthenticating,
    token,
    hasAttemptedAutoLogin,
  ])

  const authenticateWithFedCM = async () => {
    if (isFedCMAuthenticating) {
      return
    }
    setIsFedCMAuthenticating(true)

    try {
      const nonce =
        crypto.randomUUID?.() || Math.random().toString(36).substring(2)
      const credential = (await navigator.credentials.get({
        identity: {
          context: 'signin',
          providers: [
            {
              clientId: googleFedcmClientId,
              configURL: 'https://accounts.google.com/gsi/fedcm.json',
              mode: 'passive',
              params: { nonce },
            },
          ],
        },
      } as never)) as FedCMCredential | null

      if (credential?.token) {
        setToken(credential.token)
        setError(null)
      } else {
        setError(
          'Failed to retrieve FedCM credential - possibly disabled in browser',
        )
        setIsFedCMAvailable(false) // Trigger fallback
      }
    } catch (err) {
      const error = err as Error
      if (error.name === 'NotAllowedError') {
        setError(
          'FedCM was disabled in browser Site Settings or multiple requests detected',
        )
        setIsFedCMAvailable(false) // Trigger fallback to One Tap
      } else {
        setError(`FedCM authentication error: ${error.message}`)
      }
    } finally {
      setIsFedCMAuthenticating(false)
      setHasAttemptedAutoLogin(true) // Mark attempt as done
    }
  }

  const initializeGoogleOneTap = () => {
    if (!window.google?.accounts?.id || isFedCMAuthenticating) {
      return
    }

    window.google.accounts.id.initialize({
      auto_select: true,
      callback: (response: CredentialResponse) => {
        setToken(response.credential)
        setError(null)
      },
      cancel_on_tap_outside: false,
      client_id: googleFedcmClientId,
    })

    window.google.accounts.id.prompt()
    setHasAttemptedAutoLogin(true) // Mark attempt as done after prompting
  }

  const signInWithOAuth = ({
    redirectPath,
    segmentAnonymousId,
  }: {
    redirectPath: string
    segmentAnonymousId?: string
  }) => {
    const redirectUri = new URL('https://accounts.google.com/o/oauth2/auth')
    redirectUri.searchParams.set('client_id', googleOauthClientId)
    redirectUri.searchParams.set('redirect_uri', googleOauthRedirectUri)
    redirectUri.searchParams.set('response_type', 'token')
    redirectUri.searchParams.set('scope', 'email profile')
    redirectUri.searchParams.set('prompt', 'consent')
    redirectUri.searchParams.set('include_granted_scopes', 'true')
    const state = {
      redirectPath,
      segmentAnonymousId,
    }
    const statePayload = JSON.stringify(state)
    if (state) {
      redirectUri.searchParams.set('state', statePayload) // TODO: Probably want a nonce here too
    }
    window.location.href = redirectUri.toString()
  }

  return {
    error,
    hasAttemptedAutoLogin,
    isFedCMAuthenticating,
    isFedCMAvailable,
    isGoogleScriptLoaded,
    isOneTapAvailable,
    signInWithOAuth,
    token,
  }
}
